// Re-exporta tipos
export type {
    MetaAdAccount,
    MetaPixel,
    MetaPage,
    MetaIntegrationData,
    MetaIntegrationStatus
} from './modules/metaTypes';

export {
    startFacebookOAuthRedirect,
    getMetaOAuthRedirectUri,
    readMetaOAuthCallback,
    clearMetaOAuthUrlParams,
    clearMetaOAuthSession,
    persistMetaOAuthCallbackFromUrl,
    isMetaOAuthStateParam,
    MetaOAuthRedirectPending
} from './modules/metaSDK';

export {
    fetchMetaAdAccounts,
    fetchMetaPixels,
    fetchMetaPages,
    fetchWhatsAppBusinessAccount,
    validateMetaToken
} from './modules/metaAPI';

export {
    saveMetaIntegration,
    getMetaIntegration,
    removeMetaIntegration
} from './modules/metaRepository';

import { supabase } from '../lib/supabase';
import { throwFunctionsInvokeError } from '../lib/functionsInvoke';
import {
    startFacebookOAuthRedirect,
    getMetaOAuthRedirectUri,
    MetaOAuthRedirectPending,
    readMetaOAuthCallback,
    clearMetaOAuthUrlParams,
    clearMetaOAuthSession,
} from './modules/metaSDK';
import { fetchMetaAdAccounts, fetchMetaPages } from './modules/metaAPI';
import { MetaAdAccount, MetaPage } from './modules/metaTypes';

const loadMetaAssets = async (accessToken: string): Promise<{
    accessToken: string;
    accounts: MetaAdAccount[];
    pages: MetaPage[];
}> => {
    const [accounts, pages] = await Promise.all([
        fetchMetaAdAccounts(accessToken),
        fetchMetaPages(accessToken)
    ]);

    if (accounts.length === 0) {
        throw new Error('Nenhuma conta de anúncio encontrada. Verifique se você tem permissões adequadas.');
    }

    return { accessToken, accounts, pages };
};

/** Evita double-exchange no Strict Mode (mesma página). Não usa sessionStorage — senão trava no refresh. */
let metaExchangeInflightCode: string | null = null;

/**
 * Troca o code OAuth (redirect) por access token via Edge Function.
 */
export const completeMetaOAuthCallback = async (
    code: string,
    organizationId: string,
): Promise<{
    accessToken: string;
    accounts: MetaAdAccount[];
    pages: MetaPage[];
}> => {
    const redirectUri = getMetaOAuthRedirectUri();

    const { data, error } = await supabase.functions.invoke('meta-api', {
        body: {
            action: 'exchange-token',
            code,
            redirectUri,
            organizationId,
        }
    });

    if (error || data?.error) {
        await throwFunctionsInvokeError(data, error, 'Erro ao validar login com Meta');
    }

    const accessToken = data?.access_token as string | undefined;
    if (!accessToken) {
        throw new Error('Token de acesso não retornado pelo Meta.');
    }

    return loadMetaAssets(accessToken);
};

/**
 * Processa callback OAuth na URL (uma vez). Seguro a remount / Strict Mode.
 * Mantém ?code= até o exchange terminar para outros gates não desviarem a rota.
 */
export const resolvePendingMetaOAuth = async (): Promise<{
    accessToken: string;
    accounts: MetaAdAccount[];
    pages: MetaPage[];
    organizationId: string;
} | null> => {
    let callback: ReturnType<typeof readMetaOAuthCallback>;
    try {
        callback = readMetaOAuthCallback();
    } catch (error) {
        clearMetaOAuthUrlParams();
        clearMetaOAuthSession();
        throw error;
    }

    if (!callback) return null;

    const { code, organizationId } = callback;
    if (metaExchangeInflightCode === code) {
        return null;
    }

    metaExchangeInflightCode = code;
    sessionStorage.setItem('adlitics_meta_connecting', 'true');

    try {
        const data = await completeMetaOAuthCallback(code, organizationId);
        // Mantém ?code= e connecting até o caller fazer stash do modal
        // (evita janela em que o gate manda para o dashboard)
        return { ...data, organizationId };
    } catch (error) {
        clearMetaOAuthUrlParams();
        clearMetaOAuthSession();
        throw error;
    } finally {
        metaExchangeInflightCode = null;
    }
};

/**
 * Inicia conexão Meta Ads via OAuth redirect (Login for Business).
 */
export const connectMetaAccount = async (organizationId: string): Promise<{
    accessToken: string;
    accounts: MetaAdAccount[];
    pages: MetaPage[];
}> => {
    startFacebookOAuthRedirect(organizationId);
    throw new MetaOAuthRedirectPending();
};
